import { useEffect, useRef } from "react";

const FAST_TIMEOUT_MS = 5_000;
const SLOW_TIMEOUT_MS = 15_000;
// Consecutive calls hitting the fast limit before we switch to slow mode.
const TIMEOUTS_BEFORE_SLOW = 2;
// Consecutive calls finishing inside the fast limit before we go back to fast mode.
const FAST_SUCCESSES_TO_RECOVER = 2;

/**
 * Polls `fetchFn` on an interval, giving each call a time limit.
 *
 * The limit is 5s normally. If a couple of calls in a row hit it (slow network),
 * the limit and the polling cadence both move to 15s so calls have a chance to
 * finish. A couple of quick calls in a row moves it back to 5s.
 *
 * `fetchFn` must pass `signal` to the request and return early (without setting
 * error state) when `signal.aborted` — an aborted call is a timeout or unmount,
 * not a server failure. The poll restarts immediately whenever `fetchFn`'s
 * identity changes (e.g. a new gameId), aborting any call in flight.
 */
export function useAdaptivePolling(
  fetchFn: (signal: AbortSignal) => Promise<void>,
  intervalMs: number = FAST_TIMEOUT_MS,
) {
  const slowRef = useRef(false);
  const consecutiveTimeoutsRef = useRef(0);
  const consecutiveFastRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;

    const run = async () => {
      const startedAt = Date.now();
      const ctrl = new AbortController();
      controller = ctrl;
      let timedOut = false;
      const limitTimer = setTimeout(
        () => {
          timedOut = true;
          ctrl.abort();
        },
        slowRef.current ? SLOW_TIMEOUT_MS : FAST_TIMEOUT_MS,
      );

      try {
        await fetchFn(ctrl.signal);
      } catch {
        // fetchFn owns its own error handling
      }
      clearTimeout(limitTimer);
      if (cancelled) return;

      const elapsed = Date.now() - startedAt;
      if (timedOut) {
        consecutiveFastRef.current = 0;
        consecutiveTimeoutsRef.current += 1;
        if (consecutiveTimeoutsRef.current >= TIMEOUTS_BEFORE_SLOW) {
          slowRef.current = true;
        }
      } else {
        consecutiveTimeoutsRef.current = 0;
        if (slowRef.current) {
          consecutiveFastRef.current =
            elapsed < FAST_TIMEOUT_MS ? consecutiveFastRef.current + 1 : 0;
          if (consecutiveFastRef.current >= FAST_SUCCESSES_TO_RECOVER) {
            slowRef.current = false;
            consecutiveFastRef.current = 0;
          }
        }
      }

      const every = slowRef.current
        ? Math.max(intervalMs, SLOW_TIMEOUT_MS)
        : intervalMs;
      timer = setTimeout(run, Math.max(0, every - elapsed));
    };

    run();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller?.abort();
    };
  }, [fetchFn, intervalMs]);
}
